import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Flame, MessageCircle, BarChart3, Calendar,
  Target, Apple, Users, User, Menu, X, Award, Share2, Shield, HelpCircle, LogOut, Heart, Leaf, TrendingUp
} from 'lucide-react';
import { LogoIcon } from '@/components/Logo';
import { LanguageSelector } from '@/components/LanguageSelector';

import { BookOpen, ExternalLink } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/trigger', labelKey: 'nav.trigger', icon: Flame },
  { path: '/ai-guide', labelKey: 'nav.guidance', icon: MessageCircle },
  { path: '/journey', labelKey: 'nav.journey', icon: Award },
  { path: '/guna', labelKey: 'nav.guna', icon: BarChart3 },
  { path: '/kosha', labelKey: 'nav.kosha', icon: Leaf },
  { path: '/wellness-journey', labelKey: 'nav.wellness_journey', icon: TrendingUp },
  { path: '/reflection', labelKey: 'nav.reflection', icon: Calendar },
  { path: '/programs', labelKey: 'nav.programs', icon: Target },
  { path: '/lifestyle', labelKey: 'nav.lifestyle', icon: Apple },
  { path: '/family', labelKey: 'nav.family', icon: Users },
  { path: '/share', labelKey: 'common.share', icon: Share2 },
  { path: '/profile', labelKey: 'nav.profile', icon: User },
];

// External links that open in new tab
const EXTERNAL_NAV_ITEMS = [
  { path: '/quotes', label: 'Gita Quotes', icon: BookOpen },
  { path: '/chapters', label: 'Explore Gita Verses', icon: BookOpen },
];

const ADMIN_NAV = { path: '/admin', label: 'Admin', icon: Shield };

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:sticky top-0 left-0 z-50 h-screen w-60 bg-white/80 backdrop-blur-xl border-r border-border/50 flex flex-col transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`} data-testid="sidebar">
        {/* Logo */}
        <div className="p-5 border-b border-border/30">
          <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
            <LogoIcon />
            <span className="font-['Playfair_Display'] text-lg font-semibold text-foreground">Living Geeta</span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-1">
            {user?.role === 'superadmin' && (
              <Link
                to={ADMIN_NAV.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all mb-2 ${
                  location.pathname === ADMIN_NAV.path
                    ? 'bg-amber-100 text-amber-800 font-medium'
                    : 'text-amber-700 hover:text-amber-800 hover:bg-amber-50'
                }`}
                data-testid="nav-admin"
              >
                <ADMIN_NAV.icon className="w-4.5 h-4.5" strokeWidth={1.5} />
                {ADMIN_NAV.label}
              </Link>
            )}
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all border-l-2 ${
                    isActive
                      ? 'bg-primary/10 text-primary font-semibold border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border-transparent'
                  }`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className={`w-4.5 h-4.5 ${isActive ? 'text-primary' : ''}`} strokeWidth={isActive ? 2 : 1.5} />
                  {item.labelKey ? t(item.labelKey) : item.label}
                </Link>
              );
            })}
          </div>

          {/* External Links — Gita Quotes & Explore Verses */}
          <div className="mt-3 pt-3 border-t border-border/30">
            <p className="px-3 text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-2 font-medium">Explore Gita</p>
            {EXTERNAL_NAV_ITEMS.map(item => (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                data-testid={`nav-external-${item.path.replace('/', '')}`}
              >
                <span className="flex items-center gap-3">
                  <item.icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </span>
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
            ))}
          </div>

          {/* Support Us CTA — distinct styling */}
          <div className="mt-3 pt-3 border-t border-border/30">
            <Link
              to="/support-us"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                location.pathname === '/support-us'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                  : 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 hover:from-amber-100 hover:to-orange-100 border border-amber-200/60'
              }`}
              data-testid="nav-support-us"
            >
              <Heart className="w-4.5 h-4.5" strokeWidth={1.5} />
              {t('nav.support_us', { defaultValue: 'Support Us' })}
            </Link>
          </div>
        </nav>

        {/* Bottom User */}
        <div className="p-4 border-t border-border/30">
          {location.pathname === '/dashboard' && (
            <button
              onClick={() => {
                localStorage.removeItem('lg_walkthrough_done');
                window.dispatchEvent(new CustomEvent('start-walkthrough'));
                setSidebarOpen(false);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 mb-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
              data-testid="start-walkthrough-btn"
            >
              <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
              How to use
            </button>
          )}
          <div className="flex items-center gap-2 mb-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" strokeWidth={1.5} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <LanguageSelector />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t('common.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar — visible on all screen sizes */}
        <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-border/30 px-4 md:px-8 py-3">
          <div className="flex items-center justify-between">
            {/* Mobile: hamburger + title */}
            <button onClick={() => setSidebarOpen(true)} className="text-foreground md:hidden" data-testid="menu-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-['Playfair_Display'] text-sm font-semibold md:hidden">Living Geeta</span>
            {/* Desktop: spacer so language floats right */}
            <span className="hidden md:block" />
            {/* Language selector always visible */}
            <LanguageSelector variant="landing" />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-6 md:px-10 py-8 max-w-5xl w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
